import React from 'react'
import { formatCurrency } from 'utils/currencyFormatter'

/**
 * Unified preview card for courses and categories on the public page.
 *
 * Props:
 *   imageUrl           - image URL (no media block is rendered when both imageUrl and videoThumbnailUrl are null)
 *   videoThumbnailUrl  - video thumbnail URL (takes priority over imageUrl)
 *   name               - display name
 *   targetAudience     - optional subtitle
 *   popular            - show floating popular badge
 *   buttonLabel        - CTA button text
 *   onClick            - card click handler
 *   onButtonClick      - button click handler (defaults to onClick)
 *   colors             - { primary, primaryGradient }
 *   popularLabel       - text for popular badge (default: 'Popular')
 *
 * Price props are all optional. GroupClassList renders this same card without
 * them and must keep its current output, so every price branch is skipped when
 * startingPrice and trialPrice are both absent.
 *   startingPrice            - lowest non-trial unit price
 *   trialPrice               - lowest trial price (0 is a real free trial)
 *   currency, durationMinutes
 *   startingPriceSuffix      - unit shown below lg (e.g. '／堂起')
 *   startingPriceSuffixShort - unit shown at lg and above (e.g. '起')
 *   trialPriceLabel, minutesLabel, freeLabel
 *   hasTrialElsewhere       - a category whose trial sits on a course other than
 *                             the cheapest one: say a trial exists, without a figure
 *   trialAvailableLabel     - wording for that badge
 */
export default function CoursePreviewCard({ imageUrl, videoThumbnailUrl, name, targetAudience, description, popular, buttonLabel, onClick, onButtonClick, colors, popularLabel, studentsCount, lessonsCount, studentsLabel, lessonsLabel, metaText, startingPrice, trialPrice, currency, durationMinutes, startingPriceSuffix, startingPriceSuffixShort, trialPriceLabel, minutesLabel, freeLabel, hasTrialElsewhere, trialAvailableLabel }) {
  const displayImage = videoThumbnailUrl || imageUrl
  const hasVideo = !!videoThumbnailUrl
  const hasStartingPrice = typeof startingPrice === 'number'
  const hasTrialPrice = typeof trialPrice === 'number'
  const showPrice = hasStartingPrice || hasTrialPrice

  return (
    <div
      className="relative border border-gray-100 rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
      onClick={onClick}
    >
      {popular && (
        <span
          className="text-sm font-bold rounded-full text-white shadow-md"
          style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            padding: '4px 12px',
            backgroundColor: colors?.primary || '#3366FF',
            zIndex: 10
          }}
        >
          🔥 {popularLabel || 'Popular'}
        </span>
      )}
      {displayImage && (
        <div className="rounded-t-xl overflow-hidden relative">
          <img src={displayImage} alt={name} className="w-full h-48 object-cover" />
          {hasVideo && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="p-5">
        <h3 className="text-lg font-semibold" style={{ color: '#222222' }}>{name}</h3>
        {targetAudience && (
          <p className="text-sm mt-1" style={{ color: '#717171' }}>{targetAudience}</p>
        )}
        {description && (
          <p className="text-sm mt-1 line-clamp-2" style={{ color: '#999' }}>{description}</p>
        )}
        {metaText && (
          <p className="text-xs mt-2 font-medium" style={{ color: colors?.primary || '#3366FF' }}>{metaText}</p>
        )}
        {showPrice && (
          <div data-testid="card-price" className="mt-3">
            {hasStartingPrice && (
              <p className="text-lg font-bold" style={{ color: '#222222' }}>
                {/* A zero here is a missing price, not an offer, so it prints
                    as the amount the package modal prints. `free` stays for
                    trials, where free is a real product. */}
                {formatCurrency(startingPrice, currency || 'TWD')}
                {/* The desktop grid gives this card 240px; the full unit wraps
                    there, so the long form is mobile-only. */}
                <span className="lg:hidden text-xs font-medium ml-1" style={{ color: '#717171' }}>
                  {startingPriceSuffix}
                  {durationMinutes ? ` · ${durationMinutes} ${minutesLabel || ''}`.trimEnd() : ''}
                </span>
                <span className="hidden lg:inline text-xs font-medium ml-1" style={{ color: '#717171' }}>
                  {startingPriceSuffixShort || startingPriceSuffix}
                </span>
              </p>
            )}
            {hasTrialPrice && (
              <p className="text-xs mt-1" style={{ color: '#717171' }}>
                {trialPriceLabel} {trialPrice === 0 ? (freeLabel || formatCurrency(0, currency || 'TWD')) : formatCurrency(trialPrice, currency || 'TWD')}
              </p>
            )}
            {/* No figure here on purpose: the trial belongs to a course other
                than the one supplying the starting price, so pairing the two
                numbers would describe an offer nobody sells. The badge still
                matches what the category modal shows. */}
            {!hasTrialPrice && hasTrialElsewhere && trialAvailableLabel && (
              <p className="text-xs mt-1" style={{ color: '#717171' }}>{trialAvailableLabel}</p>
            )}
          </div>
        )}
        {(studentsCount > 0 || lessonsCount > 0) && (
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            {studentsCount > 0 && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {studentsCount} {studentsLabel || 'students'}
              </span>
            )}
            {lessonsCount > 0 && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {lessonsCount} {lessonsLabel || 'lessons completed'}
              </span>
            )}
          </div>
        )}
        <button
          className="mt-4 w-full py-2.5 rounded-xl font-semibold text-white cursor-pointer transition-opacity hover:opacity-90"
          style={{ background: colors?.primaryGradient || colors?.primary || '#3366FF' }}
          onClick={(e) => { e.stopPropagation(); (onButtonClick || onClick)?.(e) }}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  )
}
